import { dataProfileManage } from '../../snapshot/profile'
import type { PostString, PostInt, PostCfgRename, PostImportSetting, PostImportReplace, PostCfgBindShortCut, PostOnboardCfgRename } from '../../define/request'
import { deviceManager } from '../../device/device_manager'
import { delay, withTimeout } from '@libs/utils/tools'
import { HuoChaiRenDevices, JingTaiMer68Devices, JingTaiMg75Devices, VirtualPolar75, VirtualRA68 } from '@hid/define/define'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { JingTaiV2DeviceList } from '@/define/device_vendor'
import { createDecorates } from '@hid/define/define_profile'
import { ra68SideLight } from '@hid/define/define_side_light'
import type { DeviceOnboardConfigList } from '@hid/define/response'
import { OnboardMonitorSuppress } from '@hid/driver/device'

const JINGTAI_V1_SYNC_STEP_DELAY_MS = 120
const jingtaiV1OnboardConfigDevices = new Set([VirtualPolar75, ...JingTaiMg75Devices, ...JingTaiMer68Devices])

const delayBetweenSyncMessages = async () => {
  if (jingtaiV1OnboardConfigDevices.has(deviceManager.deviceID)) {
    await delay(JINGTAI_V1_SYNC_STEP_DELAY_MS)
  }
}

const ensureCurrentProfileDecorates = () => {
  if (!deviceManager.deviceDriver.save.Decorates) {
    if (deviceManager.deviceID == VirtualRA68) {
      deviceManager.deviceDriver.save.Decorates = createDecorates(0, 75, 75, 0, ra68SideLight)
    }
    if (HuoChaiRenDevices.includes(deviceManager.deviceID)) {
      deviceManager.deviceDriver.save.Decorates = createDecorates(1, 80, 80, 0, ra68SideLight)
    }
  }
}

const syncCurrentProfileToDevice = async () => {
  deviceManager.deviceDriver.setChangeProfileFlag(true)

  try {
    ensureCurrentProfileDecorates()
    await deviceManager.deviceDriver.syncConfig()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.syncLeds()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.syncDecorationLed()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.syncDynamicColor()
    await delayBetweenSyncMessages()

    if (dataProfileManage().dataProfile.LightLastIsCustom || JingTaiV2DeviceList.includes(deviceManager.deviceID)) {
      if (!specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) && !specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2)) {
        await delay(500)
      }
      await deviceManager.deviceDriver.syncCustomColor()
      await delayBetweenSyncMessages()
    }

    await deviceManager.deviceDriver.syncKeys()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.syncMacros()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.magnet?.syncSeniorkey()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.magnet?.syncKeysTravels()
    await delayBetweenSyncMessages()
    await deviceManager.deviceDriver.magnet?.syncKeysDeadZone()
  } finally {
    deviceManager.deviceDriver.setChangeProfileFlag(false)
  }
}

const buildOnboardConfigList = async (onboardConfigList?: DeviceOnboardConfigList) => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    return {
      Current: -1,
      List: [],
    }
  }

  const currentOnboardConfigList = onboardConfigList || (await deviceManager.deviceDriver.getOnboardConfigList())
  return {
    Current: currentOnboardConfigList.Current,
    List: currentOnboardConfigList.List.map((item) => ({
      ...item,
      Name: deviceManager.deviceDriver.supportsOnboardConfigRename() ? item.Name : dataProfileManage().getOnboardSlotDisplayName(item.Index) || item.Name,
      LocalConfigIndex: dataProfileManage().getProfileIndexBySlot(item.Index),
    })),
  }
}

export const handleCfgList = async () => {
  return dataProfileManage().CfgList()
}
const HANDLE_CFG_SWITCH_TIMEOUT_MS = 30000
const HANDLE_ONBOARD_OPERATION_TIMEOUT_MS = 32000
// 板载设备软切槽时，monitor 抑制时长：只需覆盖 setOnboardConfig + applyAfterSlotSwitch 的执行时间。
// 远小于整体超时（30s），避免长时间屏蔽硬件主动切换检测。
const BOARD_DEVICE_MONITOR_SUPPRESS_MS = 5000

const getCurrentOnboardSlotSafely = async () => {
  try {
    return await deviceManager.deviceDriver.getCurrentOnboardConfigIndex()
  } catch {
    return null
  }
}

const restoreOnboardSlotSafely = async (slot: number | null) => {
  if (slot === null) return
  try {
    deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.SyncSequence)
    await deviceManager.deviceDriver.setOnboardConfig(slot)
    await deviceManager.deviceDriver.applyAfterSlotSwitch()
  } catch (error) {
    console.warn('[OnboardCfg] failed to restore hardware slot after operation failure', error)
  }
}

export const handleCfgSwitch = async (data: PostInt) => {
  // 板载设备禁止切换到未绑定槽位的配置，应使用 MTOnboardCfgReplaceSlot
  if (deviceManager.deviceDriver.supportsOnboardConfigs()) {
    const targetSlot = dataProfileManage().getSlotByProfileIndex(data.Value)
    if (targetSlot === null) {
      throw new Error('Cannot switch to unbound profile on onboard device. Use MTOnboardCfgReplaceSlot instead.')
    }
  }

  // 记录切换前的配置索引，用于硬件切换失败时回滚
  const previousProfileIndex = dataProfileManage().CfgList().Current
  const list = dataProfileManage().CfgSwitch(data)

  try {
    await withTimeout(
      (async () => {
        if (deviceManager.deviceDriver.supportsOnboardConfigs()) {
          const slotIndex = dataProfileManage().getCurrentProfileSlotIndex()
          if (slotIndex !== null) {
            // 火柴人固件原生管理各槽位数据，切换时只需告知目标槽位，不推送本地配置。
            // 仅在首次初始化（needsInitialSync）时整包推送，将本地配置写入设备新槽位（如固件升级场景）。
            // 晶泰 V2 及其他板载设备：切槽后始终将本地配置整包推送到设备，确保数据一致。
            const isNativeOnboardDevice = HuoChaiRenDevices.includes(deviceManager.deviceID)
            const needsInitialSync = isNativeOnboardDevice && dataProfileManage().isSlotPendingInitialSync(slotIndex)
            if (isNativeOnboardDevice && !needsInitialSync) {
              // 正常切槽：仅切槽，不同步本地配置
              deviceManager.deviceDriver.suppressOnboardConfigMonitor(BOARD_DEVICE_MONITOR_SUPPRESS_MS)
              await deviceManager.deviceDriver.setOnboardConfig(slotIndex)
              await deviceManager.deviceDriver.applyAfterSlotSwitch()
            } else {
              // 切槽后将本地配置整包推送到设备
              deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.SyncSequence)
              await deviceManager.deviceDriver.setOnboardConfig(slotIndex)
              await deviceManager.deviceDriver.applyAfterSlotSwitch()
              await syncCurrentProfileToDevice()
              if (needsInitialSync) {
                dataProfileManage().clearSlotPendingInitialSync(slotIndex)
              }
            }
          }
        } else {
          // 非板载设备：本地配置是真相来源。
          // 切换后需要将本地配置整包推送到键盘，确保键盘数据与本地一致。
          await deviceManager.deviceDriver.applyAfterSlotSwitch()
          await syncCurrentProfileToDevice()
        }
      })(),
      HANDLE_CFG_SWITCH_TIMEOUT_MS,
      'handleCfgSwitch',
    )
  } catch (err) {
    // 硬件切换失败，回滚本地配置到切换前的状态
    if (deviceManager.deviceDriver.supportsOnboardConfigs() && previousProfileIndex !== data.Value) {
      try {
        dataProfileManage().CfgSwitch({ Value: previousProfileIndex } as PostInt)
      } catch {
        // 回滚失败时不再抛出，保留原始错误
      }
    }
    throw err
  }
  return list
}
export const handleCfgCreate = async (data: PostString) => {
  return dataProfileManage().CfgCreate(data)
}
export const handleCfgDelete = async (data: PostInt) => {
  return dataProfileManage().CfgDelete(data)
}
export const handleCfgRename = async (data: PostCfgRename) => {
  const result = dataProfileManage().CfgRename(data)
  // 若设备支持板载配置名称写入，且该配置已绑定槽位，则同步名称到硬件
  if (deviceManager.deviceDriver.supportsOnboardConfigs() && deviceManager.deviceDriver.supportsOnboardConfigRename()) {
    const slot = dataProfileManage().getSlotByProfileIndex(data.ID)
    if (slot !== null) {
      await deviceManager.deviceDriver.setOnboardConfigName(slot, data.Name)
    }
  }
  return result
}
export const handleCfgImport = async (data: PostImportSetting) => {
  return dataProfileManage().CfgImport(data)
}
export const handleCfgImportReplace = async (data: PostImportReplace) => {
  return dataProfileManage().CfgImportReplace(data)
}
export const handleCfgExport = async (data: PostInt) => {
  return dataProfileManage().CfgExport(data)
}

// 只是绑定快捷键的数据，用于回显，具体绑定快捷键的操作在exe服务中进行，因为快捷键是系统级功能且与本地配置文件绑定
export const handleCfgBindShortcut = async (data: PostCfgBindShortCut) => {
  return dataProfileManage().CfgBindShortcut(data)
}

export const handleOnboardCfgList = async (data?: { AlignCurrentProfile?: boolean }) => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    return await buildOnboardConfigList()
  }

  // 确保有 4 个槽位配置；记录本次新分配的槽位，用于首次切换时补全设备同步。
  // 场景：固件从非板载版本（如 1.15）升级到板载版本（如 1.20）后，
  // 设备上新槽位尚无用户数据，首次切换前必须先将本地配置写入设备。
  const pm = dataProfileManage()
  // 在任何 setOnboardInitialSyncDone() 调用之前捕获：
  // 只有上一个会话就已完成初始同步时才执行 marker 检测，
  // 防止「本次刚设置 done」被误判为固件重刷。
  const wasInitialSyncAlreadyDone = pm.isOnboardInitialSyncDone()
  const newlyAssignedSlots = pm.ensureSlotConfigs()
  if (newlyAssignedSlots.length > 0) {
    // 新分配的槽位：标记待初始同步，并记录 done flag 防止后续重复标记
    pm.markSlotsForInitialSync(newlyAssignedSlots)
    pm.setOnboardInitialSyncDone()
  } else if (!pm.isOnboardInitialSyncDone()) {
    // 兼容性块：槽位已分配（如迁移弹窗完成后刷新、或部分残留状态），但 done 标记未设。
    // 此时设备端各槽位可能没有用户数据，需对所有已绑定槽位执行初始同步。
    const boundSlots = ([0, 1, 2, 3] as number[]).filter((s) => pm.getProfileIndexBySlot(s) !== null)
    if (boundSlots.length > 0) {
      pm.markSlotsForInitialSync(boundSlots)
    }
    pm.setOnboardInitialSyncDone()
  }

  const onboardConfigList = await deviceManager.deviceDriver.getOnboardConfigList()
  if (data?.AlignCurrentProfile === true) {
    dataProfileManage().alignCurrentProfileToSlot(onboardConfigList.Current)

    // 仅在连接阶段（AlignCurrentProfile）检测一次固件版本，
    // 避免每次 UI 刷新列表都重复触发检测，导致持续标记待同步。
    // 判定规则：已同步过 + 记录的固件版本与当前版本不一致（固件重刷/升级）
    // → 标记所有已绑定槽位待重同步，用户切槽时由 handleCfgSwitch 整包写入。
    // 每次连接都更新记录版本号。替代旧的「内容校验和 / magic cookie」检测。
    if (HuoChaiRenDevices.includes(deviceManager.deviceID)) {
      try {
        const currentVersion = await deviceManager.deviceDriver.getVersion()
        const syncedVersion = pm.getSyncedFirmwareVersion()
        // wasInitialSyncAlreadyDone：上一会话就已完成初始同步才执行比对，
        // 防止「本次首次进入、刚设置 done」时误判为固件重刷（尚无记录版本）。
        if (wasInitialSyncAlreadyDone && syncedVersion !== null && syncedVersion !== currentVersion) {
          const boundSlots = ([0, 1, 2, 3] as number[]).filter((s) => pm.getProfileIndexBySlot(s) !== null)
          if (boundSlots.length > 0) {
            pm.markSlotsForInitialSync(boundSlots)
            console.log(`[OnboardCfgList] firmware version changed (${syncedVersion} -> ${currentVersion}), all bound slots marked for re-sync`)
          }
        }
        pm.setSyncedFirmwareVersion(currentVersion)
      } catch {
        // 版本读取异常不影响主流程
      }
    }
  }

  // 对于支持硬件名称存储的设备，将设备侧配置名同步回本地 profile，
  // 保证不同电脑连上同一个键盘后本地显示的名称与键盘内一致
  if (deviceManager.deviceDriver.supportsOnboardConfigRename()) {
    const localCfgList = dataProfileManage().CfgList()
    let nameChanged = false
    for (const item of onboardConfigList.List) {
      const profileIndex = dataProfileManage().getProfileIndexBySlot(item.Index)
      if (profileIndex !== null) {
        const localName = localCfgList.List[profileIndex]?.Name
        if (localName !== undefined && localName !== item.Name) {
          dataProfileManage().CfgRename({ ID: profileIndex, Name: item.Name })
          nameChanged = true
        }
      }
    }
    // CfgRename 内部已调用 save()，nameChanged 仅用于日志
    if (nameChanged) {
      console.log('[OnboardCfgList] synced device config names to local profiles')
    }
  }

  return await buildOnboardConfigList(onboardConfigList)
}

export const handleOnboardCfgSync = async (data: PostInt) => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    throw new Error('onboard config not supported')
  }

  const pm = dataProfileManage()
  const currentProfileIndex = pm.CfgList().Current
  const profileStateSnapshot = pm.getProfileStateSnapshot()
  const originalHardwareSlot = await getCurrentOnboardSlotSafely()
  let profileStateChanged = false

  try {
    await withTimeout(
      (async () => {
        // 若当前配置未绑定到任何槽位，先建立绑定。
        // 不做此步骤时 buildOnboardConfigList 仍返回旧映射（原配置还占据该槽位），
        // 导致 UI 显示两条数据相同的配置。
        const currentSlot = pm.getSlotByProfileIndex(currentProfileIndex)
        if (currentSlot === null) {
          pm.loadProfileIntoSlot(currentProfileIndex, data.Value)
          profileStateChanged = true
        }

        // 抑制 monitor：setOnboardConfig + 整包同步耗时较长，
        // 用 SyncSequence 窗口防止 monitor 在此期间误触发幽灵切换通知。
        deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.SyncSequence)

        // 1:1 直映射下：切到目标槽位 → 写入完整 profile（0x61）→ 写入改键/灯效等
        // syncConfig 内部通过 CMD 0x61 写入完整 profile blob，固件会重置槽位按键矩阵，
        // 确保后续 syncKeys 写入改键时不受旧槽位残留数据干扰。
        await deviceManager.deviceDriver.setOnboardConfig(data.Value)
        await deviceManager.deviceDriver.applyAfterSlotSwitch()
        await syncCurrentProfileToDevice()
        // 同步成功后清除该槽位的首次同步标记（如固件升级后的初始化标记）
        dataProfileManage().clearSlotPendingInitialSync(data.Value)
      })(),
      HANDLE_ONBOARD_OPERATION_TIMEOUT_MS,
      'handleOnboardCfgSync',
    )
  } catch (error) {
    if (profileStateChanged) {
      pm.restoreProfileStateSnapshot(profileStateSnapshot)
    }
    await restoreOnboardSlotSafely(originalHardwareSlot)
    throw error
  }
  return await buildOnboardConfigList()
}

export const handleOnboardCfgRename = async (data: PostOnboardCfgRename) => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    throw new Error('onboard config not supported')
  }

  if (deviceManager.deviceDriver.supportsOnboardConfigRename()) {
    await deviceManager.deviceDriver.setOnboardConfigName(data.Slot, data.Name)
    dataProfileManage().setOnboardSlotDisplayName(data.Slot, null)
  } else {
    dataProfileManage().setOnboardSlotDisplayName(data.Slot, data.Name)
  }

  return await buildOnboardConfigList()
}

export const handleOnboardCfgMigrationCheck = async () => {
  const pm = dataProfileManage()
  return {
    needsMigration: pm.needsMigration(),
    candidates: pm.getMigrationCandidates(),
  }
}

export const handleOnboardCfgMigrationExecute = async (data: { KeepIndices: number[] }) => {
  const pm = dataProfileManage()
  pm.executeMigration(data.KeepIndices)
  pm.ensureSlotConfigs()
  // 迁移场景：executeMigration 已将全部 4 个槽位分配完毕，ensureSlotConfigs 返回空数组。
  // 但设备端各槽位尚无本地用户数据，首次切换到任意槽位时都必须执行完整同步（CMD 0x61）。
  // 故显式标记所有 4 个槽位为待初始同步，确保 handleCfgSwitch 触发 syncCurrentProfileToDevice。
  pm.markSlotsForInitialSync([0, 1, 2, 3])
  // 设置 done flag：后续 handleOnboardCfgList 调用（如 completeMigration 内的 apiConfigList）
  // 检测到 done=true，不会重复将已绑定槽位标记为 pending，避免重复同步。
  pm.setOnboardInitialSyncDone()
  return {}
}

/**
 * 替换槽位：将未绑定配置装入指定槽位，原槽位配置变为存档
 */
export const handleOnboardCfgReplaceSlot = async (data: { ProfileIndex: number; TargetSlot: number }) => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    throw new Error('onboard config not supported')
  }

  const pm = dataProfileManage()
  const profileStateSnapshot = pm.getProfileStateSnapshot()
  const originalHardwareSlot = await getCurrentOnboardSlotSafely()

  try {
    return await withTimeout(
      (async () => {
        // 1. 交换槽位绑定：新配置获得槽位，旧配置变为存档
        pm.loadProfileIntoSlot(data.ProfileIndex, data.TargetSlot)

        // 2. 切换本地配置到新配置
        const list = pm.CfgSwitch({ Value: data.ProfileIndex })

        // 3. 抑制 monitor，防止写入过程中被误识别为硬件主动切换
        deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.SyncSequence)
        // 切换设备到目标槽位，方案商按需 reset（applyAfterSlotSwitch），再写入完整 profile
        await deviceManager.deviceDriver.setOnboardConfig(data.TargetSlot)
        await deviceManager.deviceDriver.applyAfterSlotSwitch()
        await syncCurrentProfileToDevice()

        // 若设备支持名称写入，同步新配置名到硬件槽位，避免下次读取时旧名覆盖本地名称
        if (deviceManager.deviceDriver.supportsOnboardConfigRename()) {
          const newName = pm.CfgList().List[data.ProfileIndex]?.Name
          if (newName) {
            await deviceManager.deviceDriver.setOnboardConfigName(data.TargetSlot, newName)
          }
        }

        // 替换槽位完成后清除该槽位的首次同步标记（如固件升级后的初始化标记）
        dataProfileManage().clearSlotPendingInitialSync(data.TargetSlot)

        return list
      })(),
      HANDLE_ONBOARD_OPERATION_TIMEOUT_MS,
      'handleOnboardCfgReplaceSlot',
    )
  } catch (error) {
    pm.restoreProfileStateSnapshot(profileStateSnapshot)
    await restoreOnboardSlotSafely(originalHardwareSlot)
    throw error
  }
}

/**
 * 将当前配置的切换键复制到所有其他板载配置，并逐槽下发到设备。
 */
export const handleReplicateSwitchKeys = async () => {
  if (!deviceManager.deviceDriver.supportsOnboardConfigs()) {
    throw new Error('onboard config not supported')
  }

  const pm = dataProfileManage()
  pm.replicateSwitchKeysToAllBoundProfiles()

  const originalProfileIndex = pm.CfgList().Current
  const activeProfiles = pm.getActiveProfiles()

  // 火柴人固件独立管理各槽位数据，不在此处多槽轮写硬件。
  if (HuoChaiRenDevices.includes(deviceManager.deviceID)) {
    return true
  }

  // 非原生板载设备（如晶泰 V1）：逐槽切换并下发按键到硬件
  const originalSlot = pm.getCurrentProfileSlotIndex()
  let replicateError: unknown = null

  try {
    await withTimeout(
      (async () => {
        for (const { profileIndex, slot } of activeProfiles) {
          if (profileIndex === originalProfileIndex) continue

          // 切到目标配置
          pm.CfgSwitch({ Value: profileIndex })
          // 切换硬件槽位
          deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.LongSyncSequence)
          await deviceManager.deviceDriver.setOnboardConfig(slot)

          await deviceManager.deviceDriver.applyAfterSlotSwitch()

          // 对每个目标槽位做完整配置同步（含 restore-defaults + 写入所有按键），
          // 避免 syncKeys() 因 changeProfileFlag=false 跳过 restore-defaults，
          // 导致固件槽位中残留旧键映射覆盖新写入的切换键。
          await syncCurrentProfileToDevice()
          await delayBetweenSyncMessages()
        }
      })(),
      HANDLE_ONBOARD_OPERATION_TIMEOUT_MS,
      'handleReplicateSwitchKeys',
    )
  } catch (error) {
    replicateError = error
    throw error
  } finally {
    try {
      // 恢复原始配置和槽位；复制动作的本地结果保留，失败后用户可重试下发。
      pm.CfgSwitch({ Value: originalProfileIndex })
      if (originalSlot !== null) {
        deviceManager.deviceDriver.suppressOnboardConfigMonitor(OnboardMonitorSuppress.LongSyncSequence)
        await deviceManager.deviceDriver.setOnboardConfig(originalSlot)

        await deviceManager.deviceDriver.applyAfterSlotSwitch()
      }
    } catch (error) {
      console.warn('[OnboardCfg] failed to restore original slot after replicating switch keys', error)
      if (replicateError === null) {
        throw error
      }
    }
  }

  return true
}
